import { useState } from 'react';
import type { ResumeDocument, WrittenCutFreshness } from '../../lib/api';
import {
  RESUME_TEMPLATE_META_BY_FAMILY,
  isBuiltFamily,
  type ResumeTemplateFamily,
} from '../../lib/resumeTemplateRegistry';
import { printResume } from '../../lib/resumeToPrint';
import { useTemplatePreference } from '../../lib/useTemplatePreference';
import { Card } from '../Card';
import { ExportFlow, ExportTriggerButton } from './ExportFlow';
import { ResumeView } from './ResumeView';
import styles from './CorpusResumePanel.module.css';

/** The saved family's own displayName from the shared registry meta — a
 *  plain read, never a re-derivation or a second name list (single source
 *  of truth: RESUME_TEMPLATE_META_BY_FAMILY, the exact same map ExportFlow
 *  reads for its own cards). Falls back to the raw family string only in
 *  the defensive case of a family isBuiltFamily already confirmed is known
 *  (so this should never actually reach the fallback in practice). */
function currentFamilyLabel(family: ResumeTemplateFamily): string {
  return RESUME_TEMPLATE_META_BY_FAMILY[family]?.displayName ?? family;
}

const FORMAT_LABEL: Record<string, string> = {
  au_standard: 'AU standard',
  au_selection_criteria: 'AU selection criteria',
  us_standard: 'US standard',
  gb_standard: 'GB standard',
};

// =============================================================================
// CorpusResumePanel (throughline) — the user's WHOLE career, surfaced AS a
// résumé on the Refine tab. Ported 1:1 from apps/web/src/components/resume/
// CorpusResumePanel.tsx (Jason, direct: match the EXISTING résumé format
// exactly — this is a re-skin, not a redesign): the SAME four honest states
// (loading / error / empty / document), the SAME "composed from your corpus,
// every fact traceable" framing, the SAME <ResumeView> render underneath.
//
// Job-free counterpart to a tailored résumé: no job to tailor against, so the
// document is the SERVED composition of the corpus (GET /api/corpus/resume).
// The backend owns the ordering / profile prose / skills synthesis; this panel
// only renders the served ResumeDocument through the same component that would
// print. THIN PRESENTATION — no composition here.
//
// PRINT (2026-07-05, the apply-path deliverable — Jason: the app must produce
// a résumé he can actually hand Greenhouse): resumeToPrint is now PORTED
// (lib/resumeToPrint.ts, 1:1 from apps/web + print-safe var pinning) and the
// "Print / Save as PDF" affordance below is REAL — browser print dialog →
// Save as PDF, selectable ATS-clean text.
//
// GAP 2 (SMALL-WAVE completeness pass) — .docx is no longer apps/web-only:
// lib/resumeToDocx.ts is now ported (byte-for-byte — see that file's own
// header for why no adaptation was needed) and "Download .docx" below
// triggers the same real client-side render + download apps/web's
// ResumePanel offers. Filename uses the served doc.contact.name (never a
// fabricated placeholder) through the SAME buildPrintDocumentTitle
// convention the Print button's popup title already uses.
//
// POLISH-2 (living-résumé Phase A, 2026-07-05) — THE WRITTEN CUT. `doc` is now
// WHICHEVER cut useCorpusResume decided to display (`cut`): the WRITTEN
// document by default when fresh, else the raw one — the parent already
// resolved this, so the panel stays THIN PRESENTATION (no cut-selection logic
// here beyond the toggle's own onClick). Both states are honestly labeled per
// the design brief's own framing, never silently swapped: written says "your
// story, written well — every fact yours"; raw says "the raw cut — exactly
// what you've told me." When no written cut exists (or the corpus moved since
// the last one), a Mira-voice invite/nudge offers the one-tap POST
// (`onWriteCut`) that mints it — never auto-generated, always a visible ask.
// =============================================================================

export function CorpusResumePanel({
  doc,
  resumeId,
  loading,
  error,
  onReload,
  writtenCut = 'none',
  cut = 'raw',
  hasWritten = false,
  onSetCut,
  writing = false,
  writeError = null,
  onWriteCut,
}: {
  doc: ResumeDocument | null;
  /** The real resumes.id this served document was composed into (Save to
   *  Drive re-land, 2026-07-05) — null only before the first successful
   *  load. DriveSaveButton only mounts once this is a real id (never a
   *  fabricated placeholder artifactId). */
  resumeId: string | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
  /** Whether a written cut exists/matches the current corpus — drives the
   *  invite ('none'), the "corpus moved, re-write?" nudge ('stale'), or
   *  neither ('fresh': the toggle is enough). Optional so this panel stays
   *  usable in isolation (e.g. a future test) without every caller wiring
   *  POLISH-2's full state — omitting it degrades to the pre-POLISH-2 view
   *  (raw only, no toggle, no invite). */
  writtenCut?: WrittenCutFreshness;
  /** Which cut is currently displayed in `doc`. */
  cut?: 'raw' | 'written';
  /** True once a written document is actually in hand (fresh OR just minted) —
   *  the toggle only renders once there is a second document to toggle to. */
  hasWritten?: boolean;
  onSetCut?: (cut: 'raw' | 'written') => void;
  /** True while POST /corpus/resume/write is in flight. */
  writing?: boolean;
  writeError?: string | null;
  /** Fires the explicit write/re-write action (POST /corpus/resume/write). */
  onWriteCut?: () => void;
}) {
  const [printNote, setPrintNote] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  // TEMPLATES-P2 (§7/§9/§10) — the persisted family + accent, still read
  // here so the current-template indicator (below) and the ExportFlow's
  // pre-selection both reflect the user's saved preference. Falls back to
  // the documented defaults (ats-plain / sage-teal) until the GET round-trip
  // resolves, so the panel never blocks rendering on it.
  //
  // EXPORT-FLOW (2026-07-06, docs/design/export-workflow-2026-07-06.md) —
  // the owner's verbatim ruling: "We don't need to see the resume templates
  // until we want to export, which should be a different workflow." The
  // template gallery + accent swatches + Download PDF/.docx/Save-to-Drive
  // buttons that used to live inline in this toolbar now live ONLY inside
  // ExportFlow, opened by the single Export trigger below. This reading
  // view shows CONTENT — the current family name (a line, not a control)
  // and Print from browser (the separate, labeled convenience R275 already
  // established) are all that remain here.
  const { family, accentKey, setFamily, setAccent } = useTemplatePreference();

  function handlePrint() {
    const ok = printResume('Resume');
    setPrintNote(ok ? null : 'Couldn’t open the print window — allow pop-ups for this site and try again.');
  }

  if (loading) {
    return (
      <Card className={styles.statusCard}>
        <span className={styles.spinner} aria-hidden="true" />
        I'm composing your stories into a résumé…
      </Card>
    );
  }

  if (error || !doc) {
    // One calm state for "nothing to show": a backend route that isn't live yet,
    // a thin corpus, or a transient failure. Honest + actionable, never a fake CV.
    return (
      <Card className={styles.emptyCard}>
        <div className={styles.emptyIcon} aria-hidden="true">
          {error ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
              <path d="M14 3v6h6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <div className={styles.emptyTitle}>
          {error ? 'I couldn’t put this together yet' : 'Nothing to show yet'}
        </div>
        <div className={styles.emptyBody}>
          {error
            ? error
            : 'Once your story has roles and achievements, I’ll compose them into a résumé here — ordered and summarised from what’s actually true, nothing invented.'}
        </div>
        <button type="button" className={styles.reloadButton} onClick={onReload}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M21 12a9 9 0 1 1-2.64-6.36M21 4v5h-5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Try again
        </button>
      </Card>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.toolbar}>
        <span className={styles.formatChip}>{FORMAT_LABEL[doc.formatVariant] ?? doc.formatVariant}</span>
        <span className={styles.toolbarNote}>
          Composed from your stories — order &amp; summary are derived, every fact traceable.
        </span>
        <ExportTriggerButton onClick={() => setExportOpen(true)} open={exportOpen} className={styles.reloadButton} />
        <button type="button" className={styles.reloadButton} onClick={handlePrint}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v7H6z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Print from browser
        </button>
      </div>
      {printNote ? <div className={styles.toolbarNote}>{printNote}</div> : null}

      {/* EXPORT-FLOW — the current template as a plain line, not a control
          (the owner's ruling: templates belong to the export workflow, not
          the reading flow). Only the family name is shown; picking a
          different one happens inside ExportFlow. */}
      {isBuiltFamily(family) ? (
        <div className={styles.toolbarNote}>Current look: {currentFamilyLabel(family)}</div>
      ) : null}

      <ExportFlow
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        doc={doc}
        family={family}
        accentKey={accentKey}
        onSelectFamily={setFamily}
        onSelectAccent={setAccent}
        artifactId={resumeId}
        artifactType="corpus_resume"
        displayName={doc.contact.name}
        artifactLabel="Resume"
      />

      {/* POLISH-2 — the written/raw toggle: only once a written document
          exists (fresh OR just minted) is there a second cut to switch to.
          A pure display flip, no network call — both documents already live
          in the parent's state. Both labels are honest about what they are. */}
      {hasWritten ? (
        <div className={styles.cutToggle} role="group" aria-label="Résumé version">
          <button
            type="button"
            className={cut === 'written' ? styles.cutTabActive : styles.cutTab}
            onClick={() => onSetCut?.('written')}
            aria-pressed={cut === 'written'}
          >
            Written
          </button>
          <button
            type="button"
            className={cut === 'raw' ? styles.cutTabActive : styles.cutTab}
            onClick={() => onSetCut?.('raw')}
            aria-pressed={cut === 'raw'}
          >
            Raw
          </button>
          <span className={styles.toolbarNote}>
            {cut === 'written'
              ? 'Your story, written well — every fact yours.'
              : 'The raw cut — exactly what you’ve told me.'}
          </span>
        </div>
      ) : null}

      {/* The invite (no written cut has ever been minted) or the nudge (one
          exists but the corpus moved since) — a Mira-voice ask, never an
          automatic mint. Tapping fires POST /corpus/resume/write; the SAME
          affordance re-appears every time the corpus changes again (the loop,
          not a one-shot). */}
      {!hasWritten && writtenCut === 'none' && onWriteCut ? (
        <div className={styles.writeInvite}>
          <span>Want me to write this properly? One tap.</span>
          <button
            type="button"
            className={styles.reloadButton}
            onClick={onWriteCut}
            disabled={writing}
          >
            {writing ? 'Writing…' : 'Write it'}
          </button>
        </div>
      ) : null}
      {hasWritten && writtenCut === 'stale' && onWriteCut ? (
        <div className={styles.writeInvite}>
          <span>Your story’s moved since I last wrote this — want a fresh pass?</span>
          <button
            type="button"
            className={styles.reloadButton}
            onClick={onWriteCut}
            disabled={writing}
          >
            {writing ? 'Writing…' : 'Re-write it'}
          </button>
        </div>
      ) : null}
      {writeError ? (
        <div className={styles.toolbarNote} role="alert">
          {writeError}
        </div>
      ) : null}

      {/* Live preview, scaled to fit the phone-width canvas. */}
      <div className={styles.previewFrame}>
        <div className={styles.previewScale}>
          <ResumeView doc={doc} family={isBuiltFamily(family) ? family : undefined} accentKey={accentKey} />
        </div>
      </div>
    </div>
  );
}
